import { useState } from "react";
import { Check } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Logo from "@/components/Logo";

// Chrome opens this page when someone removes the extension
// (chrome.runtime.setUninstallURL). Public: they may not be signed in.

const REASONS = [
  { id: "not_finding_contacts", label: "It didn't find the people I looked up" },
  { id: "data_wrong", label: "The emails or phones were wrong" },
  { id: "too_expensive", label: "It costs too much" },
  { id: "hard_to_use", label: "It was hard to use" },
  { id: "privacy", label: "I was worried about privacy" },
  { id: "using_other_tool", label: "I use another tool" },
  { id: "temporary", label: "I'll reinstall it later" },
  { id: "other", label: "Something else" },
];

export default function ExtensionUninstalled() {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await supabase.rpc("submit_extension_feedback", {
      p_reason: reason, p_details: details || null, p_email: email || null,
    }).then(() => {}, () => {});
    setBusy(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-background px-6 py-10 text-foreground">
      <div className="mx-auto w-full max-w-xl space-y-8">
        <Logo size={28} textClass="text-lg" />
        {sent ? (
          <div className="space-y-3" data-testid="uninstall-thanks">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-success-soft text-success"><Check className="h-6 w-6" /></span>
            <h1 className="font-display text-[32px] leading-tight tracking-[-0.02em]">Thanks for telling us</h1>
            <p className="text-[15px] leading-6 text-ink-secondary">
              We read every answer. Your account, lists and credits are still there if you come back.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="uninstall-form">
            <div>
              <h1 className="font-display text-[32px] leading-tight tracking-[-0.02em]">Sorry to see you go</h1>
              <p className="mt-2 text-[15px] leading-6 text-ink-secondary">
                The extension is removed. What made you uninstall it? It takes ten seconds.
              </p>
            </div>
            <div role="radiogroup" aria-label="Reason" className="grid gap-2">
              {REASONS.map((r) => (
                <button key={r.id} type="button" role="radio" aria-checked={reason === r.id}
                  onClick={() => setReason(r.id)} data-testid={`uninstall-reason-${r.id}`}
                  className={`flex items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                    reason === r.id ? "border-primary bg-violet-soft" : "border-border bg-surface hover:bg-surface-hover"}`}>
                  {r.label}
                  {reason === r.id && <Check className="h-4 w-4 text-violet-text" />}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <label htmlFor="details" className="text-[13px] font-medium">Anything we could have done better? (optional)</label>
              <Textarea id="details" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="uninstall-details" />
            </div>
            <div className="space-y-2">
              <label htmlFor="fb-email" className="text-[13px] font-medium">Your email, if you'd like a reply (optional)</label>
              <Input id="fb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <Button type="submit" disabled={!reason || busy} data-testid="uninstall-submit">Send feedback</Button>
          </form>
        )}
      </div>
    </div>
  );
}
